import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TreatmentSummaryService, { type MedicationStats } from '../../services/treatmentSummaryService';
interface TreatmentSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  medicationId: string;
}

// Componente exibe resumo do tratamento; parâmetros memberId e memberName reservados para uso futuro

interface MedicationInfo {
  id: string;
  member_id: string;
  remedy_name: string;
  potency: string | null;
  dosage_type: 'globules' | 'drops';
  quantity: number;
  times: string[];
  frequency: string;
  start_date: string;
  end_date: string | null;
  usage_days: number | null;
  pause_days: number | null;
  total_period_months: number | null;
  notes: string | null;
  is_active: boolean;
}

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatDate(dateString?: string | null) {
  if (!dateString) return '--';
  try {
    const [y,m,d] = dateString.split('-').map(Number);
    const dt = new Date(y, (m||1)-1, d||1);
    return dt.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return dateString;
  }
}

function frequencyDisplay(frequency?: string) {
  const f = (frequency || '').toString();
  if (f.startsWith('weekly;')) {
    const idx = Number(f.split(';')[1]);
    const day = !Number.isNaN(idx) ? weekDays[idx] : '';
    return day ? `Semanal (${day})` : 'Semanal';
  }
  if (f.startsWith('custom;')) {
    const part = f.split(';')[1];
    if (part) {
      const dayNames = part.split(',')
        .map((idx) => weekDays[Number(idx)])
        .filter(Boolean)
        .join(', ');
      return dayNames ? `Personalizado (${dayNames})` : 'Personalizado';
    }
    return 'Personalizado';
  }
  return ({
    'daily': 'Diário',
    'alternate': 'Dias alternados',
    'weekly': 'Semanal',
    'custom': 'Personalizado'
  } as Record<string, string>)[f] || f || '--';
}

function getAdherenceColor(percentage: number): string {
  if (percentage >= 90) return 'text-emerald-600';
  if (percentage >= 75) return 'text-green-600';
  if (percentage >= 60) return 'text-yellow-600';
  if (percentage >= 40) return 'text-orange-600';
  return 'text-red-600';
}

const TreatmentSummaryModal: React.FC<TreatmentSummaryModalProps> = ({
  isOpen,
  onClose,
  medicationId,
  // memberId e memberName não usados atualmente mas mantidos na interface para uso futuro
}) => {
  const [medication, setMedication] = useState<MedicationInfo | null>(null);
  const [stats, setStats] = useState<MedicationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const title = 'Tratamento Concluído';

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [med, s] = await Promise.all([
        TreatmentSummaryService.getMedication(medicationId),
        TreatmentSummaryService.getMedicationStats(medicationId)
      ]);
      setMedication(med as MedicationInfo);
      setStats(s);
    } catch (e) {
      console.error('[TreatmentSummaryModal] Falha ao carregar:', e);
      setError('Erro ao carregar resumo do tratamento');
    } finally {
      setLoading(false);
    }
  }, [medicationId]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const dosageText = useMemo(() => {
    if (!medication) return '--';
    return medication.dosage_type === 'globules'
      ? `${medication.quantity} glóbulos`
      : `${medication.quantity} gotas`;
  }, [medication]);

  const timesText = useMemo(() => {
    if (!medication) return '--';
    const t = Array.isArray(medication.times) ? medication.times : [];
    return t.length > 0 ? t.join(', ') : 'Não definido';
  }, [medication]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{title}</h2>
              {medication && (
                <p className="text-green-100 mt-1">{medication.remedy_name} {medication.potency || ''}</p>
              )}
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" aria-label="Fechar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando resumo...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button onClick={load} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">Tentar Novamente</button>
            </div>
          ) : medication && stats ? (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className={`text-xl font-bold ${getAdherenceColor(stats.adherence)}`}>{Math.round(stats.adherence)}%</div>
                  <div className="text-xs text-blue-800">Aderência</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-purple-600">{stats.points}</div>
                  <div className="text-xs text-purple-800">Pontos</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-emerald-600">{stats.taken}</div>
                  <div className="text-xs text-emerald-800">No horário</div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-yellow-600">{stats.taken_late}</div>
                  <div className="text-xs text-yellow-800">Fora do horário</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-red-600">{stats.missed}</div>
                  <div className="text-xs text-red-800">Perdidas</div>
                </div>
              </div>

              {/* Mensagem motivacional */}
              <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-300 p-5 rounded-xl mb-6 shadow-md">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 text-3xl">🎉</div>
                  <div className="flex-1">
                    <p className="text-amber-900 text-base font-medium leading-relaxed">
                      Parabéns! Agora que terminou o tratamento, é hora de procurar seu homeopata para fazer a sua atualização periódica.
                    </p>
                  </div>
                </div>
                <div className="absolute -top-1 -right-1 text-2xl">✨</div>
              </div>

              {/* Resumo do tratamento */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Resumo do Tratamento</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>• Medicamento: <span className="font-medium">{medication.remedy_name} {medication.potency || ''}</span></p>
                  <p>• Dosagem: {dosageText}</p>
                  <p>• Horários: {timesText}{Array.isArray(medication.times) && medication.times.length > 1 ? ` (${medication.times.length} vezes ao dia)` : ''}</p>
                  <p>• Frequência: {frequencyDisplay(medication.frequency)}</p>
                  <p>• Início: {formatDate(medication.start_date)}{medication.end_date ? ` • Fim: ${formatDate(medication.end_date)}` : ''}</p>
                  {typeof medication.usage_days === 'number' && (
                    <p>• Duração: {medication.usage_days} dias de uso{Number(medication.pause_days) > 0 ? `, ${medication.pause_days} dias de pausa` : ''}</p>
                  )}
                  {typeof medication.total_period_months === 'number' && (
                    <p>• Período total: {medication.total_period_months} {medication.total_period_months === 1 ? 'mês' : 'meses'}</p>
                  )}
                  {medication.notes && <p>• Observações: {medication.notes}</p>}
                </div>
              </div>

              {/* Botão de fechar */}
              <div className="text-center">
                <button onClick={onClose} className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                  Fechar Resumo
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600">Não há dados para exibir.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreatmentSummaryModal;
